<template>
  <div>
    <b-tabs
      vertical
      :animated="false"
      :class="[
        'settingTabs',
        isProjectOwner
          ? $style.projectOwnerSettingsTab
          : $style.projectCoordinatorSettingsTab,
      ]"
      v-model="settingTab"
    >
      <b-tab-item label="General">
        <div
          :key="generalSettingsTabKey"
          :class="$style.projectSettingsEditContainer"
        >
          <label class="label label-settings-lineHeight label-color"
            >Project Title</label
          >
          <b-input
            v-model="projectSettingDetail.name"
            @input="updateDisableButton('projectName')"
            custom-class="project-settings-general-input"
            :disabled="!editable"
          ></b-input>

          <label
            class="label label-settings-lineHeight label-color margin-top20"
            >Project Description</label
          >
          <b-input
            placeholder="Enter Project Description"
            v-model="projectSettingDetail.description"
            type="textarea"
            @input="updateDisableButton('projectDescription')"
            custom-class="project-settings-general-input"
            :disabled="!editable"
          >
          </b-input>

          <hr />
          <b-checkbox
            class="checkbox-size"
            @input="updateDisableButton('enableProjectGrip')"
            :value="false"
            v-model="projectSettingDetail.enableProjectGrip"
            :disabled="!editable"
          >
            Enable GRiP for Project</b-checkbox
          >
          <div class="columns margin-top60">
            <div
              class="column is-two-thirds del-archive-project-container text-color"
            >
              <a
                @click="handleUnarchiveProject"
                class="archive-button text-size bold"
                v-if="isProjectArchived"
              >
                Unarchive Project
              </a>
              <a
                @click="handleArchiveProject"
                class="archive-button text-size bold"
                v-else
              >
                Archive Project
              </a>
              <a
                v-if="isProjectOwner"
                @click="handleDeleteProject"
                class="text-size bold"
                >Delete Project</a
              >
            </div>
            <div class="column is-one-third">
              <b-button
                v-if="editable"
                :disabled="isGeneralSettingSubmitButtonDisabled"
                @click="updateGeneralSettingHandler"
                :class="$style.saveChangesBtn"
                type="is-primary"
                >Save</b-button
              >
            </div>
          </div>
        </div>
      </b-tab-item>
      <b-tab-item label="Sites" v-if="isProjectOwner">
        <div>
          <SiteSettings
            :key="siteTabKey"
            :projectDetail="projectDetail"
            class="margin-bottom80"
            :editable="editable"
          >
          </SiteSettings>
        </div>
      </b-tab-item>
      <b-tab-item label="GRIP" :disabled="gripTabDisabled"></b-tab-item>
      <b-tab-item label="Barrier" :visible="tabVisiblity">
        <BarrierSettings v-if="showBarrierTab" :key="barrierTabKey" />
      </b-tab-item>
      <b-tab-item label="Enabler" :visible="tabVisiblity">
        <EnablerSettings v-if="showEnablerTab" :key="enablerTabKey" />
      </b-tab-item>
      <b-tab-item label="Strategy" :visible="tabVisiblity">
        <StrategySettings v-if="showStrategyTab" :key="strategyTabKey" />
      </b-tab-item>
      <b-tab-item label="Domain/Category" :visible="tabVisiblity">
        <DomainCategorySettings
          v-if="showDomainCategoryTab"
          :key="domainCategoryTabKey"
        />
      </b-tab-item>
    </b-tabs>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { State, Action } from 'vuex-class';
import {
  UpdateProjectGeneralSettingRequestPayload,
  ProjectDetailPayload,
  ProjectStatus,
} from '@/store/modules/projects/types/projects.types';
import { isTruthy } from '@/jbi-shared/util/watcher.vue-decorator';
import { isEqual as _isEqual } from 'lodash';
import { ToastProgrammatic as Toast } from 'buefy';
import BaseDialog from '../../../components/base/BaseDialog.vue';
import ArchiveModalBox from '../../../components/ArchiveModalBox.vue';
import DeleteModalBox from '../../../components/DeleteModalBox.vue';
import { useUserEmail } from '@/utils/user.util';
import SiteSettings from '@/views/Project/components/SiteSettings.vue';
import DomainCategorySettings from '../../Grip/DomainCategory/DomainCategorySettings.vue';
import BarrierSettings from '../../Grip/Barrier/BarrierSettings.vue';
import EnablerSettings from '../../Grip/Enabler/EnablerSettings.vue';
import StrategySettings from '../../Grip/Strategy/StrategySettings.vue';
import { cloneData } from '@/utils/general.util';

@Component({
  components: {
    BaseDialog,
    SiteSettings,
    DomainCategorySettings,
    BarrierSettings,
    EnablerSettings,
    StrategySettings,
  },
})
export default class ProjectSettingsTab extends Vue {
  @Prop() public projectId!: number;
  @Prop() public projectDetail!: ProjectDetailPayload;
  @Prop() public projectSettingInfo!: any;
  @Prop() public isProjectOwner!: boolean;
  @Prop({ default: 0 }) public activeTab!: number;

  public settingTab: number = 0;
  public isGeneralSettingSubmitButtonDisabled: boolean = true;
  /** Form model for General tab; must stay an object (never []) so v-model paths stay valid before mount. */
  public projectSettingDetail: {
    name: string;
    description: string;
    enableProjectGrip: boolean;
  } = {
    name: '',
    description: '',
    enableProjectGrip: false,
  };
  public projectDetailInfo: any = [];
  public tabVisiblity: boolean = false;
  public gripTabDisabled: boolean = false;
  public showBarrierTab: boolean = false;
  public showEnablerTab: boolean = false;
  public showStrategyTab: boolean = false;
  public showDomainCategoryTab: boolean = false;
  /**
   * Do not read @Prop projectDetail in class field initializers: Vuex can clear projectDetail
   * during refetch (GET_PROJECT_LOADING) while the parent still mounts children for a tick.
   */
  public projectStatus: ProjectStatus = ProjectStatus.ACTIVE;
  public updateProjectStatusTo: ProjectStatus = ProjectStatus.ACTIVE;

  /** Adding unique keys for tab components for dynamically adding or removing tab components */
  public generalSettingsTabKey: number = Math.floor(Math.random() * 999);
  public siteTabKey: number = Math.floor(Math.random() * 999);
  public barrierTabKey: number = Math.floor(Math.random() * 999);
  public enablerTabKey: number = Math.floor(Math.random() * 999);
  public strategyTabKey: number = Math.floor(Math.random() * 999);
  public domainCategoryTabKey: number = Math.floor(Math.random() * 999);

  @Action('projects/getProjectDetail')
  public getProjectDetail!: (payload: { projectId: number }) => void;

  @Action('projects/publishProjectById')
  public publishProjectById!: (projectId: number) => void;

  @Action('projects/archiveProjectById')
  public archiveProjectById!: (projectId: number) => void;

  @Action('projects/deleteProjectById')
  public deleteProjectById!: (projectId: number) => void;

  @Action('projects/updateProjectGeneralSettings')
  public updateProjectGeneralSettings!: (
    params: UpdateProjectGeneralSettingRequestPayload,
  ) => void;

  @State((state) => state.projects.updatedProjectGeneralSettings)
  public updatedProjectGeneralSettings!: any;

  @State((state) => state.projects.apiState.updateProjectStatus.success)
  public updateProjectSuccess!: boolean;

  @State((state) => state.projects.projectName)
  public projectNameState!: any;

  @State((state) => state.projects.auditGripType)
  public auditGripType!: any;

  get userEmail(): string | undefined {
    return useUserEmail.call(this);
  }

  @Watch('projectDetail', { immediate: true })
  public syncStatusFromProjectDetail(
    detail: ProjectDetailPayload | undefined,
  ): void {
    if (!detail?.status) {
      return;
    }
    const next = detail.status as ProjectStatus;
    this.projectStatus = next;
    this.updateProjectStatusTo = next;
  }

  get isProjectArchived(): boolean {
    return this.projectStatus === ProjectStatus.ARCHIVED;
  }

  get editable(): boolean {
    return !this.isProjectArchived;
  }

  /**
   * Handles the logic required when a tab changes, such as resetting visibility flags, disabling / enabling the GRIP tab, and updating component keys to force re-rendering.
   * This function will be passed as the callback to useTabChangeGuard.
   *
   * @param {number} tabIndex - The index of the tab being switched to.
   */
  private handleTabContentLoading(tabIndex: number): void {
    // Reset visibility for GRIP sub-tabs initially
    this.showBarrierTab = false;
    this.showEnablerTab = false;
    this.showStrategyTab = false;
    this.showDomainCategoryTab = false;

    // Determine actual indices for roles
    const ownerGripIndex = 2;
    const ownerBarrierIndex = 3;
    const coordinatorGripIndex = 1;
    const coordinatorBarrierIndex = 2;

    // Handle GRIP tab redirection first
    if (this.isProjectOwner && tabIndex === ownerGripIndex) {
      this.gripTabDisabled = true; // Disable GRIP tab
      this.$nextTick(() => {
        if (this.settingTab === ownerGripIndex) {
          // Ensure we are still on the GRIP tab before changing
          this.settingTab = ownerBarrierIndex; // Change to Barrier tab index for Owner
        }
      });
      return; // Exit early, the guard will re-trigger for the new settingTab value
    }

    if (!this.isProjectOwner && tabIndex === coordinatorGripIndex) {
      this.gripTabDisabled = true; // Disable GRIP tab
      this.$nextTick(() => {
        if (this.settingTab === coordinatorGripIndex) {
          // Ensure we are still on the GRIP tab before changing
          this.settingTab = coordinatorBarrierIndex; // Change to Barrier tab index for Coordinator
        }
      });
      return; // Exit early, the guard will re-trigger for the new settingTab value
    }

    // Determine visibility and update keys based on the new tab index
    switch (tabIndex) {
      case 0: // General
        this.tabVisiblity = false;
        this.gripTabDisabled = false;
        this.generalSettingsTabKey += 1;
        break;
      case 1: // Owner: Sites / Coordinator: GRIP
        if (!this.isProjectOwner) {
          // Owner on Sites tab (their index 1)
          this.tabVisiblity = false;
          this.gripTabDisabled = false;
          this.siteTabKey += 1;
        }
        break;

      case 2: // Owner: GRIP (handled by redirection) / Coordinator: Barrier
        this.gripTabDisabled = true;
        if (!this.isProjectOwner) {
          this.tabVisiblity = true;
          this.showBarrierTab = true;
          this.gripTabDisabled = true;
          this.barrierTabKey += 1;
        }
        break;
      case 3: // Owner: Barrier / Coordinator: Enabler
        this.gripTabDisabled = true;
        this.tabVisiblity = true;
        if (!this.isProjectOwner) {
          // Coordinator: Strategy
          this.showStrategyTab = true;
          this.enablerTabKey += 1;
        } else {
          // Owner on Barrier Tab
          this.showBarrierTab = true;
          this.barrierTabKey += 1;
        }
        break;
      case 4: // Owner: Enabler / Coordinator: Strategy
        this.gripTabDisabled = true;
        this.tabVisiblity = true;
        if (!this.isProjectOwner) {
          this.showStrategyTab = true;
          this.strategyTabKey += 1;
        } else {
          // Project Owner: Enabler
          this.showEnablerTab = true;
          this.enablerTabKey += 1;
        }
        break;
      case 5: // Strategy (Owner only)
        this.gripTabDisabled = true;
        this.tabVisiblity = true;
        if (!this.isProjectOwner) {
          // Coordinator on Domain/Category tab (their index 5)
          this.showDomainCategoryTab = true;
          this.domainCategoryTabKey += 1;
        } else {
          // Owner on Strategy tab (their index 5)
          this.showStrategyTab = true;
          this.strategyTabKey += 1;
        }
        break;
      case 6: // Owner: Domain/Category
        if (this.isProjectOwner) {
          // Owner on Domain/Category tab (their index 6)
          this.gripTabDisabled = true;
          this.tabVisiblity = true;
          this.showDomainCategoryTab = true;
          this.domainCategoryTabKey += 1;
        }
        break;

      default:
        this.tabVisiblity = false;
        this.gripTabDisabled = false;
    }
  }

  public updateDisableButton(type: string) {
    if (type === 'projectDescription') {
      this.isGeneralSettingSubmitButtonDisabled = _isEqual(
        this.projectSettingInfo.description,
        this.projectSettingDetail.description,
      );
    } else if (type === 'enableProjectGrip') {
      this.isGeneralSettingSubmitButtonDisabled = _isEqual(
        this.projectSettingInfo.enableProjectGrip,
        this.projectSettingDetail.enableProjectGrip,
      );
    } else {
      this.isGeneralSettingSubmitButtonDisabled = _isEqual(
        this.projectSettingInfo.name,
        this.projectSettingDetail.name,
      );
    }
  }

  public handleUnarchiveProject() {
    this.$buefy.modal.open({
      parent: this,
      component: BaseDialog,
      hasModalCard: true,
      trapFocus: true,
      props: {
        title: 'Unarchive Project?',
        content: `This will enable the project and its data collection period to be edited. You may archive the project again anytime`,
        confirmButtonText: 'Cancel',
        cancelButtonText: 'Unarchive',
      },
      events: {
        cancel: () => {
          this.updateProjectStatusTo = ProjectStatus.ACTIVE;
          this.publishProjectById(this.projectDetail.id);
        },
      },
    });
  }

  public handleArchiveProject() {
    this.$buefy.modal.open({
      parent: this,
      component: ArchiveModalBox,
      hasModalCard: true,
      trapFocus: true,
      props: {
        modalTitle: `Archive this Project?`,
        modalContent: `Archiving '${this.projectSettingDetail.name}' will convert the project to view-only and disable any ongoing data collection period under this project.`,
      },
      events: {
        confirm: () => {
          this.updateProjectStatusTo = ProjectStatus.ARCHIVED;
          this.archiveProjectById(this.projectDetail.id);
        },
      },
    });
  }

  public handleDeleteProject() {
    this.$buefy.modal.open({
      parent: this,
      component: DeleteModalBox,
      hasModalCard: true,
      trapFocus: true,
      props: {
        modalTitle: 'Delete Project?',
        modalContent: `Deleting the project '${this.projectSettingDetail.name}' will remove it completely from PACES and you can no longer access any data collection period made under this project.`,
      },
      events: {
        confirm: () => {
          this.updateProjectStatusTo = ProjectStatus.DELETED;
          this.deleteProjectById(this.projectDetail.id);
        },
      },
    });
  }

  public updateGeneralSettingHandler() {
    this.updateProjectGeneralSettings({
      projectId: this.projectId,
      projectName: this.projectSettingDetail.name.trim(),
      projectDescription: this.projectSettingDetail.description
        ? this.projectSettingDetail.description.trim()
        : '',
      enableProjectGrip: this.projectSettingDetail.enableProjectGrip,
    });
  }

  @Watch('settingTab')
  /**
   * Emit an event when the internal setting tab index changes.
   * @param {number} newIndex The new index of the active internal setting tab.
   * @param {number} oldIndex The old index of the active internal setting tab.
   */
  onInternalSettingTabChange(newIndex: number, oldIndex: number) {
    if (newIndex !== oldIndex) {
      this.handleTabContentLoading(newIndex);
      this.$emit('active-vertical-tab-index-changed', newIndex);
    }
  }

  public mounted(): void {
    this.projectSettingDetail = cloneData(
      this.projectSettingInfo ?? {
        name: '',
        description: '',
        enableProjectGrip: false,
      },
    );
    this.projectDetailInfo = cloneData(this.projectDetail);
    this.settingTab = this.activeTab;

    this.handleTabContentLoading(this.settingTab);
  }

  @Watch('updatedProjectGeneralSettings')
  @isTruthy
  public updateSavedGeneralSettings(newValue: any) {
    this.isGeneralSettingSubmitButtonDisabled = true;
    const updatedProjectGeneralData = {
      title: newValue.title,
      description: newValue.description,
      enableProjectGrip: newValue.enableProjectGrip,
    };
    this.$emit('updateProjectSettings', updatedProjectGeneralData);
    Toast.open({
      message: `Changes saved`,
      position: 'is-top',
      type: 'is-dark',
    });
  }

  @Watch('updateProjectSuccess')
  @isTruthy
  public watchUpdatedStatus(): void {
    this.projectStatus = this.updateProjectStatusTo as ProjectStatus;
    this.$emit('updatedProjectStatus', this.projectStatus);
    if (this.projectStatus === ProjectStatus.DELETED) {
      this.$router.push({
        name: 'project-me',
      });
    }
  }
}
</script>

<style lang="scss" module>
.projectSettingsEditContainer {
  width: 680px;
  margin-right: auto;
  margin-left: 20px;
}
.saveChangesBtn {
  float: right;
  border-radius: 24px;
  height: 48px;
  padding: 0 calc(1em + 0.25em);
  font-weight: 700 !important;
  font-size: 18px !important;
  line-height: 22px !important;
}

.projectOwnerSettingsTab ul li:nth-child(4),
.projectOwnerSettingsTab ul li:nth-child(5),
.projectOwnerSettingsTab ul li:nth-child(6),
.projectOwnerSettingsTab ul li:nth-child(7) {
  span {
    padding-left: 1em;
  }
}

.projectCoordinatorSettingsTab ul li:nth-child(3),
.projectCoordinatorSettingsTab ul li:nth-child(4),
.projectCoordinatorSettingsTab ul li:nth-child(5),
.projectCoordinatorSettingsTab ul li:nth-child(6) {
  span {
    padding-left: 1em;
  }
}
</style>
